<template>
  <transition name="fade" appear>
    <div
      class="absolute top-0 left-0 h-full w-full flex justify-center items-center z-50 bg-n-950/50 backdrop-blur-sm p-4"
      :class="{ 'cursor-pointer': dismissable }"
      @click="dismissable && cancel()"
    >
      <Transition name="scale" appear>
        <div
          class="bg-n-0 dark:bg-n-900 rounded-lg shadow-lg p-4 text-center relative w-[31rem] max-h-[calc(100vh-2rem)] overflow-y-auto m-auto flex flex-col gap-3 text-n-800 dark:text-n-0 cursor-default"
          :class="[noLabels ? 'pb-4' : 'pb-8']"
          @click.stop
        >
          <l-icon
            v-if="!hiddenClose"
            name="close"
            class="text-n-600 dark:text-n-0 cursor-pointer self-end"
            @click="cancel()"
          />
          <div v-if="icon || image || avatar" class="flex justify-center items-center pb-2">
            <div v-if="icon" :class="(iconColor, iconAnimation)">
              <l-icon :name="icon" size="medium" />
            </div>
            <div v-else-if="image" class="flex justify-center items-center w-40 h-40">
              <img :src="image" />
            </div>
            <l-avatar v-else :value="avatar" mode="image" size="extra-large" />
          </div>
          <div v-if="title" class="font-heading text-2xl font-bold whitespace-normal">
            {{ title }}
          </div>
          <div v-if="content" class="whitespace-pre-line">
            {{ content }}
          </div>
          <div v-if="html" v-html="html"></div>
          <div v-if="subContent" class="text-n-300 whitespace-pre-line">
            {{ subContent }}
          </div>
          <slot name="extraContent"> </slot>
          <div v-if="!noLabels" class="flex gap-3 pt-6 px-8">
            <l-button
              v-if="secondaryLabel"
              type="secondary"
              class="w-full"
              :loading="loadingSecondary"
              :disabled="loading"
              @click="secondary()"
            >
              {{ secondaryLabel ?? t('ctas.cancel') }}
            </l-button>
            <l-button
              type="primary"
              class="w-full"
              @click="confirm()"
              :loading="loading"
              :disabled="loadingSecondary || disabled"
            >
              {{ mainLabel ?? t('ctas.confirm') }}
            </l-button>
          </div>
        </div>
      </Transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { LIconName } from './icon.type'
import LAvatar from './LAvatar.vue'
import LButton from './LButton.vue'
import LIcon from './LIcon.vue'

export type Props = {
  title?: string
  mainLabel?: string
  secondaryLabel?: string
  content?: string
  html?: string
  disabled?: boolean
  subContent?: string
  icon?: LIconName
  iconAnimation?: 'animate-spin'
  iconColor?: string
  noLabels?: boolean
  hiddenClose?: boolean
  dismissable?: boolean
  image?: string
  avatar?: string
  onConfirm?: () => void | Promise<void>
  onSecondary?: () => void | Promise<void>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  cancel: []
  close: []
}>()

const { t } = useI18n()

const loading = ref(false)
const loadingSecondary = ref(false)

function close() {
  emit('close')
}

function cancel() {
  emit('cancel')
  close()
}

async function secondary() {
  loadingSecondary.value = true
  await props.onSecondary?.()
  close()
  loadingSecondary.value = false
}

async function confirm() {
  loading.value = true
  await props.onConfirm?.()
  close()
  loading.value = false
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.scale-enter-active,
.scale-leave-active {
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.scale-enter-from,
.scale-leave-to {
  transform: scale(0.95);
}

.scale-enter-to,
.scale-leave-from {
  transform: scale(1);
}
</style>
